import React, { type ReactNode, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { Box } from '@atlaskit/primitives';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import PortalRequestGroups from '@atlassian/jira-servicedesk-portal-settings-request-groups/src/ui/index.tsx';
import CustomerPortalClassicEntry from '@atlassian/jira-servicedesk-settings-customer-portal-v2/src/utils/classic-entry';
import { generatePath, useRouter } from '@atlassian/react-resource-router';
import type { AppProps } from '../../common/types';
import messages from './messages';

type TabData = {
	key: string;
	label: ReactNode;
	content: ReactNode;
}[];

export const PORTAL_GROUPS_FRAGMENT = 'portal-groups';

const useTabsState = () => {
	const [
		{
			match: {
				params: { projectKey, subPath },
				path,
			},
		},
		{ push },
	] = useRouter();
	const tabIndexForSubPath = Number(subPath === PORTAL_GROUPS_FRAGMENT);

	const setSelectedTabIndex = useCallback(
		(tabIndex: number) => {
			const currentTabIndex = tabIndexForSubPath;

			if (tabIndex !== currentTabIndex) {
				const url = generatePath(path, {
					projectKey,
					subPath: tabIndex === 0 ? undefined : PORTAL_GROUPS_FRAGMENT,
				});

				push(url);
			}
		},
		[path, projectKey, push, tabIndexForSubPath],
	);

	return [tabIndexForSubPath, setSelectedTabIndex] as const;
};

const PortalTabs = (props: AppProps) => {
	const { formatMessage } = useIntl();
	const [selectedTabIndex, setSelectedTabIndex] = useTabsState();

	const tabs = useMemo<TabData>(
		() => [
			{
				key: 'portal-config',
				label: formatMessage(messages.portalConfigTabLabel),
				content: (
					<TabChildWrapper>
						<CustomerPortalClassicEntry {...props} baseUrl="" showPageHeader={false} />
					</TabChildWrapper>
				),
			},
			{
				key: 'portal-groups',
				label: formatMessage(messages.portalRequestGroupTabLabel),
				content: (
					<TabChildWrapper>
						<PortalRequestGroups projectId={props.projectId} />
					</TabChildWrapper>
				),
			},
		],
		[props, formatMessage],
	);
	return (
		<Box paddingInline="space.100">
			<Tabs id="portal-tabs" selected={selectedTabIndex} onChange={setSelectedTabIndex}>
				<TabList>
					{tabs.map((tab) => (
						<Tab key={`${tab.key}-tab`}>{tab.label}</Tab>
					))}
				</TabList>
				{tabs.map((tab) => (
					<TabPanel key={`${tab.key}-panel`}>{tab.content}</TabPanel>
				))}
			</Tabs>
		</Box>
	);
};

export default PortalTabs;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabChildWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 0,
	width: '100%',
	paddingTop: token('space.300', '24px'),
	zIndex: 0,
});
